/**
 * Dashboard API — portfolio aggregation reads.
 * Uses the shared `api` instance (base '/api/v1', 401-silent-refresh,
 * envelope-normalised errors). Returns the unwrapped `data` payload.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from './client';
import type {
  PortfolioDashboardResponse,
  AccountDashboardResponse,
  HealthTrendResponse,
  DeliveryDashboardResponse,
} from '@/types/domain';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
  meta?: Record<string, unknown>;
}

/**
 * Every dashboard query key prefix. Kept here next to the useQuery hooks so a
 * new dashboard query is registered for invalidation in one place.
 */
const DASHBOARD_KEY_PREFIXES = [
  ['portfolio-dashboard'],
  ['dashboard-account'],
  ['dashboard-workload'],
  ['dashboard-bottlenecks'],
  ['dashboard-capacity'],
  ['dashboard-health-trend'],
  ['dashboard-delivery'],
] as const;

/**
 * Invalidate every dashboard query (by key prefix) so the Portfolio / Account /
 * Capacity / health-trend views refetch after a mutation that changes their
 * inputs — status, % complete, RAG, schedule finish dates, capacity, or open-RAID
 * counts. Call from any mutation hook that mutates dashboard-visible data.
 */
export function invalidateDashboards(qc: QueryClient): void {
  for (const queryKey of DASHBOARD_KEY_PREFIXES) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** GET /dashboard/portfolio — RAG summary for every non-cancelled project. */
export async function fetchPortfolioDashboard(): Promise<PortfolioDashboardResponse> {
  const res = await api.get<ApiEnvelope<PortfolioDashboardResponse>>('/dashboard/portfolio');
  return res.data.data;
}

// ---------------------------------------------------------------------------
// Workload / Capacity types
// ---------------------------------------------------------------------------

export interface WorkloadSummary {
  memberId: string;
  memberName: string;
  crCount: number;
  totalAllocatedMd: number;
  nextFreeDate: string;
  bookedThrough: string;
}

export interface Bottleneck {
  memberId: string;
  memberName: string;
  nextFreeDate: string;
  backlogMd: number;
  crCount: number;
}

export type UtilizationBand = 'RED' | 'AMBER' | 'GREEN';

export interface UtilizationMonth {
  year: number;
  month: number;
  bookedDays: number;
  availableDays: number;
  utilizationPct: number;
  band: UtilizationBand;
}

export interface MemberUtilization {
  memberId: string;
  memberName: string;
  months: UtilizationMonth[];
}

export interface UtilizationHeatmapResponse {
  members: MemberUtilization[];
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

// Pass the selected account (or null for all) so the workload views scope to
// the resources on that account's CRs; the id is part of the cache key.
function accountParams(accountId?: string | null) {
  return accountId ? { params: { accountId } } : undefined;
}

/** GET /dashboard/workload — per-resource CR count, allocated MD, next free date. */
export function useWorkload(accountId?: string | null) {
  return useQuery<WorkloadSummary[]>({
    queryKey: ['dashboard-workload', accountId ?? null],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<WorkloadSummary[]>>('/dashboard/workload', accountParams(accountId));
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

/** GET /dashboard/bottlenecks — members with the most backlog ordered by farthest nextFreeDate. */
export function useBottlenecks(accountId?: string | null) {
  return useQuery<Bottleneck[]>({
    queryKey: ['dashboard-bottlenecks', accountId ?? null],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<Bottleneck[]>>('/dashboard/bottlenecks', accountParams(accountId));
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

/** GET /dashboard/capacity — month-by-month utilisation heatmap for every member. */
export function useUtilizationHeatmap(accountId?: string | null) {
  return useQuery<UtilizationHeatmapResponse>({
    queryKey: ['dashboard-capacity', accountId ?? null],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<UtilizationHeatmapResponse>>('/dashboard/capacity', accountParams(accountId));
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

/** GET /dashboard/account/{id} — RAG summary + projects for a single account. */
export function useAccountDashboard(accountId: string) {
  return useQuery<AccountDashboardResponse>({
    queryKey: ['dashboard-account', accountId],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<AccountDashboardResponse>>(
        `/dashboard/account/${accountId}`,
      );
      return res.data.data;
    },
    enabled: Boolean(accountId),
    staleTime: 30_000,
  });
}

/** GET /dashboard/health-trend?projectId={id} — health RAG + % complete over time. */
export function useHealthTrend(projectId: string) {
  return useQuery<HealthTrendResponse>({
    queryKey: ['dashboard-health-trend', projectId],
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<HealthTrendResponse>>(
        `/dashboard/health-trend?projectId=${projectId}`,
      );
      return res.data.data;
    },
    enabled: Boolean(projectId),
    staleTime: 60_000,
  });
}

/** GET /dashboard/delivery — CR status counts, done-last-30, weekly throughput. */
export async function fetchDeliveryDashboard(accountId?: string | null): Promise<DeliveryDashboardResponse> {
  const res = await api.get<ApiEnvelope<DeliveryDashboardResponse>>('/dashboard/delivery', accountParams(accountId));
  return res.data.data;
}

/** Delivery console — CR status mix, 30-day done count, weekly throughput. */
export function useDeliveryDashboard(accountId?: string | null) {
  return useQuery({
    queryKey: ['dashboard-delivery', accountId ?? null],
    queryFn: () => fetchDeliveryDashboard(accountId),
    staleTime: 60_000,
  });
}
